import { useState } from 'react';
import { fullPageRedirect } from '@hungryroot/environment';
import { ArrowLeft } from '@hungryroot/icons';
import { Button } from '@hungryroot/ramen';
import { StatusCode } from '@hungryroot/rest';
import {
  postHoldAccount,
  type HoldAccountData,
} from '@hungryroot/rest/api/hold_customer_account/post';
import { CreditMessage } from '../../components/CreditMessage/CreditMessage';
import { TextSection } from '../../components/TextSection/TextSection';
import { ControlRadioGroup } from '../../Controls/ControlRadioGroup/ControlRadioGroup';
import { useHoldTimePeriods } from '../../hooks/useHoldTimePeriods';
import { iosPostMessage } from '../../utils/iosPostMessage';
import styles from './Pause.module.css';
import pageStyles from '../page.module.css';

async function acceptHoldAccount(data: HoldAccountData) {
  const resp = await postHoldAccount(data);

  if (resp.status !== StatusCode.SuccessOK) {
    throw new Error(JSON.stringify(resp.data, null, 2));
  }

  return resp;
}

interface Props {
  testid?: string;
  onNext: () => void;
}

async function onOfferAccepted(value: number) {
  const res = await acceptHoldAccount({ hold_time_weeks: value.toString() });

  if (res.status === StatusCode.SuccessOK) {
    // Make message available to iOS
    iosPostMessage({ hold_account: true });
    return fullPageRedirect('/myhungryroot/');
  }
}

export function Pause(props: Props) {
  const { testid, onNext } = props;
  const [value, setValue] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const { data: holdTimePeriods } = useHoldTimePeriods();
  return (
    <div data-testid={testid} className={pageStyles.container}>
      <Button
        buttonType="link"
        to="/account/settings/"
        style="default"
        testid={`${testid}-prev`}
        size="medium"
        prominence="tertiary"
        icon={<ArrowLeft />}
        iconPosition="left"
        textPrimary="Back to Account Settings"
        className={pageStyles.back}
      />
      <TextSection
        testid={`${testid}-text-section`}
        heading="Need a break?"
        body="Take a pause. We’ll remind you a week before your deliveries start up again."
        className={pageStyles.text}
      />
      <CreditMessage
        testid={`${testid}-credit-message`}
        className={pageStyles.credit}
      />
      <div className={pageStyles.content}>
        <div className={pageStyles['content-inner']}>
          <ControlRadioGroup
            testid={`${testid}-radio-group`}
            options={holdTimePeriods || []}
            value={value}
            onChange={setValue}
          />
          <Button
            onClick={() => {
              if (!value) {
                return;
              }
              setIsLoading(true);
              onOfferAccepted(value);
            }}
            style={value ? 'default' : 'disabled'}
            testid={`${testid}-accept`}
            size="medium"
            prominence="primary"
            textPrimary="PAUSE MY DELIVERIES"
            className={styles.accept}
            loading={isLoading}
          />
        </div>
      </div>
      <Button
        onClick={onNext}
        style="default"
        testid={`${testid}-next`}
        size="medium"
        prominence="secondary"
        textPrimary="Continue to Cancel"
        className={pageStyles.confirm}
      />
    </div>
  );
}
